import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CRIMINAL_COUNTRIES } from '../../../../mocks/cases';
import type { CriminalCertificate, DocumentReviewStatus } from '../../../../mocks/cases';
import { useState } from 'react';
import { reviewStatusBadgeCls, reviewStatusLabel } from './utils';
import { DOCUMENT_FILE_ACCEPT } from '../../../../utils/documentUploadPolicy';

/* ─── Per-country selector dropdown ─── */
export function CountrySelector({ onAdd, exclude = [] }: {
  onAdd: (country: string) => void;
  exclude?: string[];
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const filtered = CRIMINAL_COUNTRIES.filter(
    (c) => !exclude.includes(c) && c.toLowerCase().includes(search.toLowerCase()),
  );
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="w-full flex items-center justify-between border border-slate-200 rounded-lg px-3 py-2 text-[13px] text-slate-700 bg-white cursor-pointer hover:border-slate-300 transition-colors"
      >
        <span className="text-slate-500">Add country…</span>
        <i className={`ri-arrow-down-s-line text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div
          className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg overflow-hidden"
          style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.10)' }}
        >
          <div className="p-2 border-b border-slate-100">
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search…"
              className="w-full px-3 py-1.5 text-[12px] border border-slate-200 rounded-md outline-none focus:border-emerald-400 bg-white text-slate-900 placeholder:text-slate-400 [color-scheme:light]"
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="px-3 py-2 text-[12px] text-slate-500">No results</p>
            ) : filtered.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => { onAdd(c); setOpen(false); setSearch(''); }}
                className="w-full text-left px-3 py-1.5 text-[13px] text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors cursor-pointer"
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Per-country criminal certificate card ─── */
export function CriminalCertCard({ country, cert, onUpdate, onRemove, onView }: {
  country: string;
  cert: CriminalCertificate | undefined;
  onUpdate: (changes: Partial<CriminalCertificate>) => void;
  onRemove: () => void;
  onView?: () => void;
}) {
  const { t } = useTranslation();
  const fileRef = useRef<HTMLInputElement>(null);

  const isUploaded = cert?.uploaded;
  const isRequested = cert?.certRequestedNotReceived;

  return (
    <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
      {/* ── Header ── */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50/60">
        <div className="flex items-center min-w-0">
          <span className="text-[13px] font-semibold text-slate-800">{country}</span>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <label className="flex items-center gap-2 cursor-pointer text-[13px] text-slate-600 hover:text-slate-800 select-none">
            <input
              type="checkbox"
              checked={!!isRequested}
              onChange={(e) => {
                if (e.target.checked) {
                  onUpdate({ certRequestedNotReceived: true, uploaded: false, uploadedFile: '' });
                } else {
                  onUpdate({ certRequestedNotReceived: false, requestProofFile: '' });
                }
              }}
              className="w-4 h-4 rounded border-slate-300 cursor-pointer accent-emerald-600"
            />
            Unavailable
          </label>
          <button
            type="button"
            onClick={onRemove}
            className="w-6 h-6 flex items-center justify-center text-slate-300 hover:text-rose-500 transition-colors cursor-pointer shrink-0"
          >
            <i className="ri-close-line text-base" />
          </button>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="p-4 space-y-3">
        {isUploaded ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2.5 flex flex-col gap-2 min-[420px]:flex-row min-[420px]:items-center">
            <i className="ri-file-check-line text-emerald-500 shrink-0 text-base" />
            <div className="flex-1 min-w-0 flex items-center gap-2">
              <span className="min-w-0 text-[13px] text-slate-700 truncate">{cert?.uploadedFile}</span>
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${reviewStatusBadgeCls(cert?.reviewStatus ?? 'pending')}`}>
                {reviewStatusLabel(cert?.reviewStatus ?? 'pending')}
              </span>
            </div>
            <button
              type="button"
              onClick={() => onView?.()}
              className="min-h-[36px] text-[12px] text-slate-600 font-medium border border-slate-200 rounded-md px-2 py-1 cursor-pointer shrink-0 whitespace-nowrap bg-white hover:bg-slate-50"
            >
              View
            </button>
            <button
              type="button"
              onClick={() => onUpdate({ uploaded: false, uploadedFile: '' })}
              className="min-h-[36px] text-[12px] text-emerald-700 underline shrink-0 cursor-pointer whitespace-nowrap"
            >
              Change
            </button>
          </div>
        ) : isRequested && cert?.requestProofFile ? (
          <div className="bg-teal-50 border border-teal-200 rounded-xl px-3 py-2.5 flex flex-col gap-2 min-[420px]:flex-row min-[420px]:items-center">
            <i className="ri-file-warning-line text-teal-500 shrink-0 text-base" />
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-medium text-slate-700 truncate">
                {cert?.requestProofFile}
              </p>
              <p className="text-[11px] text-slate-500">Proof of request</p>
            </div>
            <button
              type="button"
              onClick={() => onUpdate({ requestProofFile: '' })}
              className="min-h-[36px] text-[12px] text-teal-700 underline shrink-0 cursor-pointer whitespace-nowrap"
            >
              Change
            </button>
          </div>
        ) : (
          <div className="flex">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="w-full min-h-[44px] border border-slate-200 rounded-xl flex items-center justify-center gap-2 text-sm text-slate-600 hover:border-slate-300 hover:bg-slate-50 transition-colors cursor-pointer"
            >
              <i className="ri-file-upload-line" />
              {t('docUpload.chooseFile', { defaultValue: 'Choose file' })}
            </button>
          </div>
        )}
      </div>

      {/* Single hidden file input - routes based on unavailable state */}
      <input
        ref={fileRef}
        type="file"
        accept={DOCUMENT_FILE_ACCEPT}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          if (cert?.certRequestedNotReceived) {
            onUpdate({ certRequestedNotReceived: true, requestProofFile: file.name, uploaded: false, uploadedFile: '' });
          } else {
            onUpdate({ uploaded: true, uploadedFile: file.name, uploadedAt: new Date().toISOString().slice(0, 10), certRequestedNotReceived: false, requestProofFile: '' });
          }
          e.target.value = '';
        }}
      />
    </div>
  );
}
